import { Problem } from '@iq/contracts/v1/errors';

/**
 * Turn anything thrown by openapi-fetch or other callers into a short,
 * user-presentable string.
 *
 * Priority order:
 *   1. RFC 7807 `Problem` envelope (`detail` > `title`), Zod-parsed.
 *   2. Better Auth error body (`{ message, code }`).
 *   3. Native `Error.message`.
 *   4. String fallback for primitives.
 *   5. Generic 'Something went wrong' for anything else.
 *
 * Validation envelopes (HTTP 422) get a compact field summary so the user
 * sees which field was wrong instead of a vague "Bad Request".
 */
export function describeError(error: unknown, fallback = 'Something went wrong'): string {
  if (error == null) return fallback;

  const parsed = Problem.safeParse(error);
  if (parsed.success) {
    const p = parsed.data;
    if (p.status === 422 && Array.isArray(p.errors) && p.errors.length > 0) {
      const summary = p.errors
        .slice(0, 3)
        .map((e) => `${e.path.replace(/^body\./, '')}: ${e.message}`)
        .join('; ');
      return `${p.title}. ${summary}`;
    }
    return p.detail ?? p.title;
  }

  if (typeof error === 'object' && error !== null) {
    const o = error as { message?: unknown; detail?: unknown; title?: unknown };
    if (typeof o.message === 'string' && o.message.length > 0) return o.message;
    if (typeof o.detail === 'string' && o.detail.length > 0) return o.detail;
    if (typeof o.title === 'string' && o.title.length > 0) return o.title;
  }

  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error.length > 0) return error;
  return fallback;
}

/**
 * Structured RFC 7807 fields pulled out of a chat-stream error. The thrown
 * payload from the chat transport is the `application/problem+json` response
 * body of the failed POST. Parsing it through the same `Problem` Zod schema
 * as `describeError` lets the chat-bubble error UI render `title` / `detail`
 * / `status` / `requestId` as distinct fields instead of dumping raw JSON.
 */
export interface ParsedChatError {
  title: string;
  detail?: string;
  status?: number;
  requestId?: string;
}

export function parseChatErrorMessage(
  input: unknown,
  fallback = 'Something went wrong',
): ParsedChatError {
  // Pull out the candidate string to JSON.parse.
  let raw: string | undefined;
  if (typeof input === 'string') raw = input;
  else if (input instanceof Error) raw = input.message;
  else if (typeof input === 'object' && input !== null && 'message' in input) {
    const m = (input as { message?: unknown }).message;
    if (typeof m === 'string') raw = m;
  }

  if (raw?.trim().startsWith('{')) {
    try {
      const parsed = Problem.safeParse(JSON.parse(raw));
      if (parsed.success) {
        const p = parsed.data;
        const out: ParsedChatError = { title: p.title };
        const detail =
          p.status === 422 && Array.isArray(p.errors) && p.errors.length > 0
            ? p.errors
                .slice(0, 3)
                .map((e) => `${e.path.replace(/^body\./, '')}: ${e.message}`)
                .join('; ')
            : p.detail;
        if (detail) out.detail = detail;
        if (typeof p.status === 'number') out.status = p.status;
        if (typeof p.requestId === 'string') out.requestId = p.requestId;
        return out;
      }
    } catch {
      // Fall through to string fallback below.
    }
  }

  if (raw && raw.length > 0) return { title: raw };
  return { title: fallback };
}

/**
 * Numeric HTTP status when the thrown value carries one — used to decide
 * retry and 401-driven navigation. Returns undefined for non-HTTP errors
 * (network failures, validation throws).
 */
export function extractStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  if (!('status' in error)) return undefined;
  const raw = (error as { status: unknown }).status;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : undefined;
}
